import type { MouseEvent } from 'react';
import React from 'react';
import { Breakpoint } from '@noths/polaris-client-ribbons-base';
import { Picture, PictureSource } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';

import * as styles from './HeroCarouselItem.styles';

export interface HeroCarouselItemProps {
  alt: string;
  /** Heading element to render. The first slide is an `h1`; the rest are `h2`. Defaults to `h2`. */
  headingLevel?: 'h1' | 'h2';
  headingLine1: string;
  headingLine2?: string;
  /**
   * `hidden` keeps the heading in the document for screen readers and search engines but clips it
   * out of view, and drops the gradient that exists only to keep a visible heading legible. For
   * imagery that already carries its own headline.
   */
  headingPosition: 'top' | 'bottom' | 'hidden';
  /** When set, the whole image links here — used for the active slide's CTA destination. */
  href?: string;
  /** Image for screens 768px and wider. Falls back to `imageSmallScreen` if omitted. */
  imageLargeScreen?: string;
  /** Image for screens narrower than 768px, and the default `src`. */
  imageSmallScreen: string;
  onImageClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  priority?: boolean;
  /** The only hero on show rather than one of several slides, which softens the corner to half. */
  standalone?: boolean;
}

/**
 * The sliding part of a hero slide: the image with its heading overlay. The paragraph, T&Cs and
 * CTA are rendered separately by `HeroCarouselCaption` so they can swap in place rather than slide.
 *
 * Uses the design-system `Picture`: `PictureSource` applies a `min-width` query, so the large-screen
 * image sits in a `min-width: M` (768px) source and the small-screen one is the default `src`. That
 * matches the widths the two Contentstack assets are specified against, and the width this
 * component's own styles change the image height at. The first slide loads eagerly.
 */
export const HeroCarouselItem = ({
  alt,
  headingLevel = 'h2',
  headingLine1,
  headingLine2,
  headingPosition,
  href,
  imageLargeScreen,
  imageSmallScreen,
  onImageClick,
  priority = false,
  standalone = false,
}: HeroCarouselItemProps) => {
  // Keep h1/h2 as literal tags rather than a dynamic `<Heading>`: babel-plugin-styled-components
  // hoists a `styled(...)` wrapper for the `css` prop to module scope, where a tag held in a local
  // variable isn't in scope (ReferenceError at runtime).
  const isHeadingHidden = headingPosition === 'hidden';
  const isHeadingTop = headingPosition === 'top';
  const headingCss = isHeadingHidden
    ? [visuallyHidden]
    : [styles.heading, isHeadingTop ? styles.headingTop : styles.headingBottom];
  const headingContent = (
    <>
      {/*
       * Decorative blur layer behind the gradient; masked so it fades into the image (see styles).
       * Skipped for a hidden heading: with nothing on show there is nothing to make legible, and
       * darkening the image anyway would be a visible change with no purpose.
       */}
      {!isHeadingHidden && (
        <div
          aria-hidden="true"
          css={[styles.blurLayer, isHeadingTop ? styles.blurLayerTop : styles.blurLayerBottom]}
          data-testid="heading-backdrop"
        >
          <div css={styles.blurInner} />
        </div>
      )}
      <span>{headingLine1}</span>
      {headingLine2 && <span>{headingLine2}</span>}
    </>
  );

  return (
    <div css={[styles.imageWrapper, standalone && styles.imageWrapperStandalone]}>
      {/*
       * The anchor is always rendered but only links when `href` is set (the active slide), so the
       * image is never remounted as slides change — wrapping/unwrapping `<Picture>` would reset its
       * load state and flash the placeholder. The heading sibling is `pointer-events: none` so the
       * whole image, including the area behind the heading, is clickable.
       */}
      <Picture
        alt={alt}
        fetchPriority={priority ? 'high' : undefined}
        imgCss={styles.image}
        lazyLoading={!priority}
        src={imageSmallScreen}
      >
        <PictureSource minBreakpoint={Breakpoint.M} srcSet={imageLargeScreen ?? imageSmallScreen} />
      </Picture>
      {/*
       * Active slide only: a stretched link over the image (kept separate from <Picture> so the
       * image never remounts, and so inactive slides render no anchor at all). Labelled by the
       * image alt via the visually-hidden span.
       */}
      {href && (
        <a css={styles.imageLink} href={href} onClick={onImageClick}>
          <span css={visuallyHidden}>{alt}</span>
        </a>
      )}
      {headingLevel === 'h1' ? (
        <h1 css={headingCss}>{headingContent}</h1>
      ) : (
        <h2 css={headingCss}>{headingContent}</h2>
      )}
    </div>
  );
};
